import { useEffect, useMemo, useState } from 'react';
import useActions from '../../hooks/service-actions.hook';
import { useEntity } from '../../hooks/use-entity.hook';
import useLoaders from '../../hooks/use-loaders.hook';
import useUserDetails from '../../hooks/user-details.hook';
import { ColorPaletteEffectActions } from '../services/color-palette/color-palette.service';
import { Entities } from '../store/entities/entities.enum';
import { Loaders } from '../store/loaders/loaders.enum';
import stringUtils from '../../Utils/string.utils';
import { ColorType } from '../../constants/color-picker.constants';
import { HexColorPicker } from "react-colorful";
import './color-picker.styles.scss';
import ChevronXSIcon from '../../Components/icons/chevron-xs.icon';
import { Spinner } from 'react-bootstrap';
import BaseCheckIcon from '../../Components/icons/base-ckeck.icon';
import SuccessSignIcon from '../../Components/icons/success-sign.icon';
import TrashCanIcon from '../../Components/icons/trash-can.icon';
import ContentLoader from 'react-content-loader';

const ColorPicker = ({
  defaultColor,
  isThemeColors = false,
  themesColors,
  isClosedPlatformColors = false,
  onSelect = (color) => { },
  colorValidation,
  fetchDataOnMount = true,
}) => {
  const { clientId } = useUserDetails();
  const { entityData: colorPalette } = useEntity({ entityType: Entities.ColorPalette, entityId: clientId });
  const { getColorPalette, updateCustomColor, deleteCustomColor } = useActions(ColorPaletteEffectActions);
  const { isLoading: isUpdateCustomColorLoading } = useLoaders({ loaders: [Loaders.UpdateCustomColorLoader] });
  const { isLoading: isDeleteCustomColorLoading } = useLoaders({ loaders: [Loaders.DeleteCustomColorLoader] });
  const [primaryColors, setPrimaryColors] = useState([]);
  const [customColors, setCustomColors] = useState([]);

  const [color, setColor] = useState("#aabbcc");
  const [showPlatformColors, setShowPlatformColors] = useState(true);
  const [colorError, setColorError] = useState(false);
  const [selectedColorId, setSelectedColorId] = useState(null);

  const currentCustomColors = useMemo(() => {
    const limitedCustomColors = customColors.slice(0, 7);
    return Array.from({ length: 7 }, (_, index) => limitedCustomColors[index] || null);
  }, [customColors]);

  const customColorValue = useMemo(() => {
    return color ? color.slice(1) : '';
  }, [color]);

  const onAddCustomColor = () => {
    if (!customColorValue) return;
    if (!stringUtils.isValidHex(color)) {
      setColorError(true);
      return;
    }
    updateCustomColor({ clientId, HexCode: color });
  }

  const onColorChange = (color) => {
    setColor(color?.HexCode);
    setSelectedColorId(color?.Id);
  }

  const onDeleteCustomColor = (colorId) => {
    deleteCustomColor({ clientId, colorId });
  }

  useEffect(() => {
    if (!defaultColor || !stringUtils.isValidHex(defaultColor)) return;
    setColor(defaultColor);
  }, [defaultColor]);

  useEffect(() => {
    if (fetchDataOnMount) {
      getColorPalette({ clientId });
    }
  }, []);

  useEffect(() => {
    const allColors = Object.values(colorPalette || {});
    if (!allColors.length) return;
    const primaryColors = allColors.filter((color) => color?.Type === ColorType.Primary);
    const customColors = allColors.filter((color) => color?.Type === ColorType.Custom);
    setPrimaryColors(primaryColors);
    setCustomColors(customColors);
  }, [colorPalette]);

  useEffect(() => {
    setShowPlatformColors(!isClosedPlatformColors);
  }, [isClosedPlatformColors])

  return (
    <div className="color-picker">
      <div className="menu-body">
        <HexColorPicker color={color} onChange={(color) => onColorChange({ HexCode: color })} />
        <div className="platform-colors">
          <div
            className="title-container pointer"
            onClick={() => setShowPlatformColors(!showPlatformColors)}
          >
            <h4 className="m-0">
              Platform Colors
            </h4>
            <ChevronXSIcon
              width="8px"
              height="8px"
              className={`${showPlatformColors ? 'rotate180' : ''}`}
            />
          </div>
          {showPlatformColors && (
            !colorPalette ? (
              <ColorsLoader />
            ) : (
              <div className="colors-list">
                {primaryColors?.map((color, index) => (
                  <ColorBlock
                    key={index}
                    color={color}
                    onClick={() => onColorChange(color)}
                    selectedColorId={selectedColorId}
                    isThemeColors={isThemeColors}
                  />
                ))}
              </div>
            )
          )}
        </div>
        <div className="platform-colors">
          <div className="title-container">
            <h4 className="m-0">
              {isThemeColors ? 'Theme Colors' : 'Custom Colors'}
            </h4>
          </div>
          {!colorPalette ? (
            <ColorsLoader length={7} />
          ) : (
            <div className="colors-list">
              {(isThemeColors ? themesColors : currentCustomColors).map((color, index) => (
                <ColorBlock
                  key={index}
                  color={color}
                  selectedColorId={selectedColorId}
                  isThemeColors={isThemeColors}
                  onClick={() => {
                    if (color === null) return;
                    onColorChange(color);
                  }}
                  onDelete={() => onDeleteCustomColor(color?.Id)}
                  isCustomColors
                  isDeleteLoading={isDeleteCustomColorLoading}
                />
              ))}
            </div>
          )}
        </div>
        <div className="add-custom-color-container">
          {customColors.length < 7 && (
            <button
              className="add-btn f-white"
              onClick={() => {
                onAddCustomColor();
              }}
              disabled={isUpdateCustomColorLoading}
            >
              {isUpdateCustomColorLoading ? (
                <Spinner
                  animation="border"
                  variant="light"
                  size="sm"
                  className="f-white"
                  style={{ width: '12px', height: '12px' }}
                />
              ) : (
                <BaseCheckIcon />
              )}
            </button>
          )}
          <div
            className={`${colorError ? 'input-error' : ''} d-flex align-items-center radius-4 grey-border p-2 flex-grow-1 mr-1 highlight-input-with-grey-border thin-border`}
          >
            <div className="f-darkgrey f-500">
              #
            </div>
            <input
              tabIndex={0}
              type="text"
              className="bg-transparent flex-grow-1 border-none pl-2 "
              value={customColorValue}
              onChange={(e) => {
                setColor(`#${e.target.value}`);
                setColorError(false);
              }}
            />
          </div>
        </div>
      </div>
      <hr />
      <div className="menu-footer">
        <button
          className="select-btn"
          onClick={() => {
            if (!stringUtils.isValidHex(color)) {
              setColorError(true);
              return;
            }
            if (colorValidation && !colorValidation(color)) {
              setColorError(true);
              return;
            }
            onSelect(color);
          }}
        >
          Select
        </button>
      </div>
    </div>
  )
}

export default ColorPicker;

const ColorBlock = ({ color, onClick = () => { }, selectedColorId, isThemeColors, isCustomColors, onDelete = () => { }, isDeleteLoading = false }) => {
  return (
    <div className="d-flex flex-column gap-4 position-relative">
      <div
        className={`block ${color === null ? 'none' : ''}`}
        style={{ backgroundColor: color?.HexCode }}
        onClick={onClick}
      />
      {color && selectedColorId === color?.Id && (
        <>
          <div className="position-absolute" style={{ top: '5px', right: '10px' }}>
            {isDeleteLoading ? (
              <Spinner
                animation="border"
                variant="light"
                size="sm"
                style={{
                  width: "12px",
                  height: "12px",
                }}
              />
            ) : (
              <SuccessSignIcon className="fill-current f-white" />
            )}
          </div>
          {!isThemeColors && isCustomColors && (
            <div className="d-flex justify-content-center">
              <div onClick={onDelete} className="pointer">
                <TrashCanIcon />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

const ColorsLoader = ({ length = 21 }) => {
  return (
    <div className="colors-list">
      {Array.from({ length }, (_, index) => (
        <ContentLoader
          key={index}
          width={'32px'}
          height={'32px'}
        >
          <rect className="block" x="0" y="0" rx="4px" ry="4px" width="100%" height="100%" />
        </ContentLoader>
      ))}
    </div>
  )
}